/* ================================================================
   DUNE+TIDE — Theme JS
   Cart AJAX · Drawer · Fadeup · Mobile Nav
   ================================================================ */

'use strict';

// ── CART STATE ──────────────────────────────────────────────────
const DT = {
  cartOpen: false,

  // Show toast notification
  toast(message, duration = 2800) {
    const el = document.getElementById('dtToast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('show');
    clearTimeout(DT._toastTimer);
    DT._toastTimer = setTimeout(() => el.classList.remove('show'), duration);
  },

  // Open cart drawer
  openCart() {
    document.getElementById('cartDrawer')?.classList.add('open');
    document.getElementById('cartOverlay')?.classList.add('open');
    document.getElementById('bagBtn')?.setAttribute('aria-expanded', 'true');
    DT.cartOpen = true;
    document.body.style.overflow = 'hidden';
  },

  // Close cart drawer
  closeCart() {
    document.getElementById('cartDrawer')?.classList.remove('open');
    document.getElementById('cartOverlay')?.classList.remove('open');
    document.getElementById('bagBtn')?.setAttribute('aria-expanded', 'false');
    DT.cartOpen = false;
    document.body.style.overflow = '';
  },

  // Fetch and re-render cart body
  async refreshCart() {
    try {
      const res = await fetch('/cart.json');
      const cart = await res.json();
      DT.updateCartCount(cart.item_count);
      await DT.renderCartBody(cart);
    } catch (e) {
      console.error('Cart refresh failed', e);
    }
  },

  updateCartCount(count) {
    document.querySelectorAll('#bagCount, #cartCount').forEach(el => {
      el.textContent = count;
    });
  },

  async renderCartBody(cart) {
    const body = document.getElementById('cartBody');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty">
          <p>Your bag is empty.</p>
          <a href="/" class="btn">Start shopping <span class="arr"></span></a>
        </div>`;
      const subtotalEl = document.getElementById('cartSubtotal');
      if (subtotalEl) subtotalEl.textContent = DT.formatMoney(0);
      return;
    }

    const items = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item-fig">
          ${item.image
            ? `<img src="${item.image}" alt="${DT.esc(item.product_title)}" loading="lazy">`
            : '<div class="ph" style="position:absolute;inset:0;"></div>'}
        </div>
        <div class="cart-item-info">
          <div class="ci-name">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="ci-var">${item.variant_title}</div>` : ''}
          <div class="ci-row">
            <div class="qty">
              <button class="qbtn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
              <span class="qn">${item.quantity}</span>
              <button class="qbtn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
            </div>
            <div class="ci-px">${DT.formatMoney(item.final_line_price)}</div>
          </div>
        </div>
      </div>`).join('');

    body.innerHTML = items;
    DT.bindQtyButtons();

    const subtotalEl = document.getElementById('cartSubtotal');
    if (subtotalEl) subtotalEl.textContent = DT.formatMoney(cart.total_price);
  },

  bindQtyButtons() {
    document.querySelectorAll('.qbtn').forEach(btn => {
      btn.addEventListener('click', async function() {
        const key = this.dataset.key;
        const action = this.dataset.action;
        const qnEl = this.parentElement.querySelector('.qn');
        const currentQty = parseInt(qnEl?.textContent || '1');
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        await DT.updateCartItem(key, newQty);
      });
    });
  },

  async updateCartItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      DT.updateCartCount(cart.item_count);
      await DT.renderCartBody(cart);
    } catch (e) {
      DT.toast('Could not update bag — please try again.');
    }
  },

  async addToCart(variantId, quantity = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await DT.refreshCart();
      DT.openCart();
      DT.toast('Added to bag');
    } catch (e) {
      DT.toast('Could not add to bag — please try again.');
    }
  },

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  },

  esc(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
};

// ── INIT ────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {

  // Ensure checkout links always point to /checkout
  // (guards against Liquid routes.checkout rendering empty in some CDN-cached contexts)
  document.querySelectorAll('.cart-go, a[data-checkout]').forEach(el => {
    if (!el.getAttribute('href') || el.getAttribute('href') === '') {
      el.setAttribute('href', '/checkout');
    }
  });

  // Cart drawer triggers
  document.getElementById('bagBtn')?.addEventListener('click', () => {
    DT.cartOpen ? DT.closeCart() : DT.openCart();
  });
  document.getElementById('cartClose')?.addEventListener('click', DT.closeCart);
  document.getElementById('cartContinue')?.addEventListener('click', DT.closeCart);
  document.getElementById('cartOverlay')?.addEventListener('click', DT.closeCart);

  // Escape key closes drawer
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && DT.cartOpen) DT.closeCart();
  });

  // Product form — intercept add to cart
  const productForm = document.getElementById('ProductForm');
  if (productForm) {
    productForm.addEventListener('submit', async function(e) {
      e.preventDefault();
      const variantId = document.getElementById('ProductVariantId')?.value;
      if (!variantId) { DT.toast('Please select a size'); return; }
      const atcBtn = document.getElementById('AddToCart');
      if (atcBtn) atcBtn.disabled = true;
      await DT.addToCart(variantId);
      if (atcBtn) atcBtn.disabled = false;
    });
  }

  // Quick-add buttons on product cards
  document.querySelectorAll('.pcard-quickadd').forEach(btn => {
    btn.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      const productUrl = btn.dataset.productUrl;
      if (productUrl) window.location.href = productUrl;
    });
  });

  // Mobile nav toggle
  const mobileBtn = document.getElementById('mobileMenuBtn');
  const mobileNav = document.getElementById('mobileNav');
  if (mobileBtn && mobileNav) {
    mobileBtn.addEventListener('click', () => {
      const isOpen = mobileNav.classList.toggle('open');
      mobileBtn.setAttribute('aria-expanded', isOpen);
      mobileNav.setAttribute('aria-hidden', !isOpen);
    });
  }

  // Fadeup observer
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.fadeup').forEach(el => observer.observe(el));

  // Bind initial qty buttons if cart drawer is pre-rendered with items
  DT.bindQtyButtons();

  // Scroll-based header class (optional subtle shrink on scroll)
  let lastScroll = 0;
  const header = document.getElementById('topnav');
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    if (header) {
      header.classList.toggle('scrolled', y > 40);
    }
    lastScroll = y;
  }, { passive: true });

});
